'use client';

/**
 * PremiumFooter — V124.2 closing tagline + decorative gradient line.
 *
 * Renders `premium.tagline` (max 120 chars) in an italic serif style
 * (Georgia with serif fallback — adds an editorial / luxury feel that
 * contrasts with the sans-serif body). Below the tagline, a thin
 * cyan→violet gradient line acts as a visual closer.
 *
 * Micro-animation: fade-in on scroll into view (IntersectionObserver).
 * Honors `prefers-reduced-motion` — falls back to immediate render with
 * no transition.
 *
 * Skip behavior: renders `null` when `tagline` is empty.
 *
 * Anti-P-383: ≤ 150 L. Anti-P-041/042 compliant.
 */

import { useEffect, useRef, useState } from 'react';

export interface PremiumFooterProps {
  tagline: string;
}

export function PremiumFooter({ tagline }: PremiumFooterProps) {
  const text = tagline?.trim();
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node || !text) return;
    // Respect prefers-reduced-motion: render immediately, skip the observer.
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) {
      setVisible(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.2 },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [text]);

  if (!text) return null;

  return (
    <footer
      ref={ref}
      className="px-6 sm:px-8 mt-10 mb-6 text-center"
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(8px)',
        transition: 'opacity 500ms ease-out, transform 500ms ease-out',
      }}
    >
      <p
        className="text-base sm:text-lg text-[#E0E0E0] italic"
        style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}
      >
        {text}
      </p>
      <div
        aria-hidden="true"
        className="mx-auto mt-4 h-px w-32 bg-[linear-gradient(to_right,transparent,#06B6D4,#8B5CF6,transparent)]"
      />
    </footer>
  );
}
